import CancelModalButton from 'components/Custom/CancelModalButton'
import CustomModalFooter from 'components/Custom/CustomModalFooter'
import CustomModalHeader from 'components/Custom/CustomModalHeader'
import SubmitModalButton from 'components/Custom/SubmitModalButton'
import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Input, Modal, ModalBody } from 'reactstrap'
import { updateFormSettings } from 'store/Forms/actions'
import { fetchSlackChannels } from 'store/SlackChannels/actions'
import Select from 'views/pages/Select'

function SettingsModal({ showModal, setShowModal, formId }) {
  const dispatch = useDispatch()
  const { forms: aForms, assignedForms } = useSelector((store) => store.form)
  const { channels } = useSelector((store) => store.slackChannels)

  let forms = [...aForms, ...assignedForms]

  const currentForm =
    forms && forms.length ? forms.find((f) => f?.id === formId) : null

  const [remindUsers, setRemindUsers] = useState(
    currentForm?.remind_to_submit ?? 0
  )
  const [emailFrequency, setEmailFrequency] = useState(
    currentForm?.email_frequency
  )
  const [remindViaSlack, setRemindViaSlack] = useState(
    currentForm?.remind_to_submit_slack ?? 0
  )
  const [selectedSlackChannels, setSelectedSlackChannels] = useState([])

  useEffect(() => {
    if (showModal) {
      dispatch(fetchSlackChannels())
    }
  }, [showModal, dispatch])

  // Update selectedSlackChannels when currentForm or channels change
  useEffect(() => {
    if (currentForm?.slack_channels && channels && channels.length > 0) {
      // If slack_channels is an array of IDs, find the corresponding channel objects
      if (Array.isArray(currentForm.slack_channels)) {
        const selectedChannels = currentForm.slack_channels
          .map((channelId) => {
            // Handle both ID format and object format
            if (typeof channelId === 'object' && channelId.id) {
              return channelId
            }
            // If it's just an ID, find the channel object
            return channels.find((channel) => channel.id === channelId)
          })
          .filter(Boolean) // Remove any undefined values

        setSelectedSlackChannels(selectedChannels)
      }
    } else if (!currentForm?.slack_channels) {
      setSelectedSlackChannels([])
    }
  }, [currentForm, channels])
  return (
    <>
      <Modal
        isOpen={showModal}
        toggle={() => setShowModal(false)}
        size='md'
        centered
        scrollable
      >
        <CustomModalHeader
          title={'Form Settings'}
          toggle={() => {
            setShowModal(false)
          }}
        />
        <ModalBody className='scrollbar justify-content-center text-center'>
          <div className='d-flex mb-3'>
            <label className='custom-toggle mr-4'>
              <input
                id='remind_to_submit'
                type='checkbox'
                name='remind_to_submit'
                onChange={() => setRemindUsers(remindUsers ? 0 : 1)}
                checked={remindUsers}
              />
              <span className='custom-toggle-slider rounded-circle' />
            </label>
            <label className=' form-control-label text-darker'>
              Remind users to submit form via email
            </label>
          </div>
          <div className='d-flex mb-3'>
            <label className='custom-toggle mr-4'>
              <input
                id='remind_via_slack'
                type='checkbox'
                name='remind_via_slack'
                onChange={() => setRemindViaSlack(remindViaSlack ? 0 : 1)}
                checked={remindViaSlack}
              />
              <span className='custom-toggle-slider rounded-circle' />
            </label>
            <label className='form-control-label text-darker'>
              Remind users to submit form via Slack
            </label>
          </div>

          {remindViaSlack ? (
            <div className='mt-3 text-left'>
              <label className='form-control-label text-darker mb-2'>
                Select Slack Channels *
              </label>
              <Select
                options={channels || []}
                selectedOptions={selectedSlackChannels}
                onChange={(e) => {
                  const selectedChannels = e.map((option) => option.option)
                  setSelectedSlackChannels(selectedChannels)
                }}
                multi={true}
                valueKey='id'
                nameKey='name'
                placeholder='Choose Slack channels...'
                style={{
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  minHeight: '38px',
                }}
              />
              {selectedSlackChannels.length === 0 && remindViaSlack && (
                <small className='text-danger mt-1 d-block'>
                  Please select at least one Slack channel
                </small>
              )}
            </div>
          ) : null}

          <div className={`d-flex align-items-center mt-3`}>
            <Input
              name='period'
              onChange={(e) =>
                setEmailFrequency(
                  `${e.target.value} ${
                    emailFrequency?.split(' ')?.at(1)
                      ? emailFrequency?.split(' ')?.at(1)
                      : 'd'
                  }`
                )
              }
              // value={props.rewardPoints}
              defaultValue={
                emailFrequency?.split(' ')?.at(0)
                  ? emailFrequency?.split(' ')?.at(0)
                  : 1
              }
              className='gray-bg-input mr-2'
              type='number'
              // placeholder={'1'}
              min={1}
              style={{ height: '38px', width: '60px' }}
            />
            <Select
              options={[
                { name: 'Days', value: 'd' },
                { name: 'Weeks', value: 'w' },
              ]}
              onChange={(e) => {
                if (e.option.value === 'd')
                  setEmailFrequency(`${emailFrequency?.split(' ')?.at(0)} d`)

                if (e.option.value === 'w')
                  setEmailFrequency(`${emailFrequency?.split(' ')?.at(0)} w`)
              }}
              placeholder={
                emailFrequency?.split(' ')?.at(1) === 'd' ? 'Days' : 'Weeks'
              }
              selectedOption={
                emailFrequency?.split(' ')?.at(1) === 'd'
                  ? { name: 'Days', value: 'd' }
                  : { name: 'Weeks', value: 'w' }
              }
            />
            <label className='mt-1 form-control-label text-darker ml-4'>
              Frequency
            </label>
          </div>
        </ModalBody>

        <CustomModalFooter>
          <SubmitModalButton
            customFn={() => {
              // Validate slack channels if slack reminder is enabled
              if (remindViaSlack && selectedSlackChannels.length === 0) {
                return // Don't submit if no channels selected
              }

              dispatch(
                updateFormSettings(formId, {
                  remind_to_submit: remindUsers,
                  email_frequency: emailFrequency,
                  remind_to_submit_slack: remindViaSlack,
                  slack_channels: remindViaSlack
                    ? selectedSlackChannels.map((el) => el.id)
                    : [],
                })
              )
              setShowModal(false)
            }}
          />
          <CancelModalButton customFn={() => setShowModal(false)} />
        </CustomModalFooter>
      </Modal>
    </>
  )
}

export default SettingsModal
